import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  ChevronDown,
  ChevronUp,
  Clock,
  Hammer,
  Loader2,
  Mic,
  MicOff,
  Phone,
  Radio,
  Sparkles,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useCall, useCallEvents, type CallEvent } from '../../hooks/useCalls';
import { buildTranscript, fmtCallTs as fmtTs, type TranscriptTurn } from '../../lib/buildTranscript';
import { cn } from '../../lib/utils';

/**
 * Per-call horizontal pipeline view.
 *
 * Each event becomes a card. User-side cards sit above the centerline,
 * assistant cards below. Click a card to expand a detail panel showing
 * the full payload (system prompt, full LLM response, tool args, etc).
 */
export function CallTimelinePage() {
  const { callId, uid } = useParams<{ callId: string; uid?: string }>();
  const call = useCall(callId);
  const events = useCallEvents(callId);

  const grouped = React.useMemo(() => groupEvents(events.events), [events.events]);
  const [openId, setOpenId] = React.useState<string | null>(null);
  const openEvent = React.useMemo(
    () => events.events.find((e) => e.id === openId) ?? null,
    [events.events, openId],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Button asChild size="sm" variant="ghost" className="mb-2 h-7 px-2">
            <Link to={uid ? `/admin/users/${uid}/settings/calls` : '/settings/calls'}>
              <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Alle Anrufe
            </Link>
          </Button>
          <h2 className="text-lg font-semibold tracking-tight">
            Anruf {callId?.slice(0, 8) ?? '—'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {call.data?.startedAt?.toDate?.()?.toLocaleString('de-DE') ?? '—'}
            {call.data?.callerNumber && ` · von ${call.data.callerNumber}`}
            {call.data?.provider && ` · ${call.data.provider}`}
            {call.data?.llmModelUsed && ` · ${call.data.llmModelUsed}`}
          </p>
        </div>
        <KpiStrip
          turns={(call.data?.userTurnCount ?? 0) + (call.data?.assistantTurnCount ?? 0)}
          tools={call.data?.toolCallCount ?? 0}
          fillerInjections={call.data?.fillerInjections ?? 0}
          latencyP50={call.data?.latencyP50 ?? 0}
          duration={call.data?.durationSec ?? 0}
        />
      </div>

      <TranscriptCard events={events.events} loading={events.loading} />

      <Card>
        <CardHeader>
          <CardTitle>Pipeline-Ansicht</CardTitle>
          <CardDescription>
            Zeitlich sortiert von links nach rechts. Oben Kunde, unten KI. Klick → Detail.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {events.loading ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Events …
            </div>
          ) : grouped.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
              Keine Events.
            </div>
          ) : (
            <Timeline grouped={grouped} openId={openId} setOpenId={setOpenId} />
          )}
        </CardContent>
      </Card>

      {openEvent && <DetailPanel event={openEvent} onClose={() => setOpenId(null)} />}
    </div>
  );
}

// `buildTranscript`, `fmtTs`, `TranscriptTurn` live in
// `lib/buildTranscript.ts` so the ticket detail panel can render the
// same dedup'd transcript without duplicating the dedup logic.

function TranscriptCard({
  events,
  loading,
}: {
  events: CallEvent[];
  loading: boolean;
}) {
  const turns = React.useMemo(() => buildTranscript(events), [events]);
  const anyAssistantText = turns.some(
    (t) => t.speaker === 'assistant' && !t.textMissing,
  );
  const missingAssistantCount = turns.filter(
    (t) => t.speaker === 'assistant' && t.textMissing,
  ).length;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Transkript</CardTitle>
            <CardDescription>
              Was Kunde und KI tatsächlich gesagt haben — chronologisch.
              Tool-Aufrufe sind inline eingebettet.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Transkript …
          </div>
        ) : turns.length === 0 ? (
          <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
            Kein Sprach-Content erfasst.
          </div>
        ) : (
          <>
            {!anyAssistantText && missingAssistantCount > 0 && (
              <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                Hinweis: Für diesen Anruf wurde noch kein KI-Sprachtext mitprotokolliert
                (Pipecat-Frame-Bug). Der Observer-Fix greift ab dem nächsten Anruf.
                Die Tool-Aufrufe unten zeigen, was die KI inhaltlich entschieden hat.
              </div>
            )}
            <div className="space-y-2">
              {turns.map((t) => (
                <TranscriptRow key={t.id} turn={t} />
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function TranscriptRow({ turn }: { turn: TranscriptTurn }) {
  const isUser = turn.speaker === 'user';
  const isAssist = turn.speaker === 'assistant';
  const isTool = turn.speaker === 'tool';
  return (
    <div
      className={cn(
        'flex gap-3 rounded-md border px-3 py-2 text-sm',
        isUser && 'border-sky-500/40 bg-sky-500/5',
        isAssist && 'border-primary/40 bg-primary/5',
        isTool && 'border-amber-500/30 bg-amber-500/5',
      )}
    >
      <div className="flex shrink-0 flex-col items-center gap-1 pt-0.5">
        {isUser && <Mic className="h-4 w-4 text-sky-400" />}
        {isAssist && <Bot className="h-4 w-4 text-primary" />}
        {isTool && <Hammer className="h-4 w-4 text-amber-400" />}
        <span className="font-mono text-[10px] text-muted-foreground">
          {fmtTs(turn.ts)}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'whitespace-pre-wrap break-words',
            turn.textMissing && 'italic text-muted-foreground',
          )}
        >
          {turn.text}
        </div>
        {turn.meta && (
          <div className="mt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
            {turn.meta}
          </div>
        )}
      </div>
    </div>
  );
}

interface GroupedEvent {
  id: string;
  ts: number;
  speaker: 'user' | 'assistant' | 'system' | 'tool';
  iconLabel: string;
  Icon: React.ComponentType<{ className?: string }>;
  primary: string;
  secondary?: string;
  variant?: 'live' | 'muted' | 'default';
  raw: CallEvent;
}

function groupEvents(events: CallEvent[]): GroupedEvent[] {
  const out: GroupedEvent[] = [];
  // Aggregate noisy events (bot_started / tts_started fire multiple times
  // due to pipeline-internal links). Keep one per (type, second-bucket).
  const seen = new Set<string>();
  for (const e of events) {
    const ts = e.ts_ms ?? 0;
    const bucketKey = `${e.type}|${Math.floor(ts / 200)}`;
    if (NOISY_TYPES.has(e.type) && seen.has(bucketKey)) continue;
    seen.add(bucketKey);
    const m = mapEvent(e);
    if (!m) continue;
    out.push({ id: e.id, ts, raw: e, ...m });
  }
  return out;
}

const NOISY_TYPES = new Set(['bot_started_speaking', 'bot_stopped_speaking', 'tts_started']);

function mapEvent(e: CallEvent): Omit<GroupedEvent, 'id' | 'ts' | 'raw'> | null {
  const data = (e.data ?? {}) as Record<string, unknown>;
  switch (e.type) {
    case 'call_started':
      return {
        speaker: 'system',
        Icon: Phone,
        iconLabel: 'Anruf',
        primary: 'Anruf gestartet',
        secondary: `${data.provider ?? ''} · ${data.from ?? ''}`,
      };
    case 'call_ended':
      return {
        speaker: 'system',
        Icon: Phone,
        iconLabel: 'Ende',
        primary: 'Anruf beendet',
        secondary: data.durationSec ? `${data.durationSec}s` : undefined,
      };
    case 'user_started_speaking':
      return {
        speaker: 'user',
        Icon: Mic,
        iconLabel: 'Kunde',
        primary: 'Kunde spricht …',
        variant: 'muted',
      };
    case 'user_stopped_speaking':
      return {
        speaker: 'user',
        Icon: MicOff,
        iconLabel: 'Kunde',
        primary: 'Kunde fertig',
        secondary: data.durationMs ? `${data.durationMs}ms` : undefined,
        variant: 'muted',
      };
    case 'user_transcript':
      return {
        speaker: 'user',
        Icon: Mic,
        iconLabel: 'Kunde',
        primary: typeof data.text === 'string' && data.text ? data.text : '(leer)',
      };
    case 'llm_first_token_latency':
      return {
        speaker: 'system',
        Icon: Clock,
        iconLabel: 'Latenz',
        primary: `LLM-Antwortzeit: ${data.ms ?? 0}ms`,
        variant: 'muted',
      };
    case 'llm_response_complete': {
      const text = typeof data.text === 'string' ? data.text : '';
      const ms = typeof data.durationMs === 'number' ? data.durationMs : null;
      return {
        speaker: 'assistant',
        Icon: Bot,
        iconLabel: 'KI-Antwort',
        primary: text || 'Antwort generiert',
        secondary: ms != null ? `${ms} ms` : undefined,
      };
    }
    case 'assistant_message_committed': {
      const text = typeof data.text === 'string' ? data.text : '';
      return {
        speaker: 'assistant',
        Icon: Bot,
        iconLabel: 'KI-Antwort',
        primary: text || 'Assistant-Message',
      };
    }
    case 'tool_call':
      return {
        speaker: 'tool',
        Icon: Hammer,
        iconLabel: 'Tool',
        primary: `Tool: ${data.name ?? '?'}`,
        secondary: shortJson(data.args),
      };
    case 'tool_result':
      return {
        speaker: 'tool',
        Icon: Hammer,
        iconLabel: 'Tool-Ergebnis',
        primary: `Ergebnis: ${data.name ?? '?'}`,
        secondary: shortJson(data.result),
      };
    case 'tts_started':
      return {
        speaker: 'assistant',
        Icon: Volume2,
        iconLabel: 'KI spricht',
        primary: 'TTS startet',
        variant: 'muted',
      };
    case 'tts_stopped': {
      const text = typeof data.text === 'string' ? data.text : '';
      return {
        speaker: 'assistant',
        Icon: VolumeX,
        iconLabel: 'TTS gesprochen',
        primary: text || 'TTS Ende',
        secondary: data.durationMs ? `${data.durationMs}ms` : undefined,
        variant: text ? 'default' : 'muted',
      };
    }
    case 'bot_started_speaking':
      return {
        speaker: 'assistant',
        Icon: Radio,
        iconLabel: 'Bot',
        primary: 'Bot spricht',
        variant: 'muted',
      };
    case 'bot_stopped_speaking':
      return null;
    case 'filler_played':
      return {
        speaker: 'assistant',
        Icon: Sparkles,
        iconLabel: 'Filler',
        primary: 'Filler eingespielt',
        secondary: typeof data.category === 'string' ? data.category : undefined,
      };
    case 'pipeline_end_frame':
      return {
        speaker: 'system',
        Icon: Phone,
        iconLabel: 'Pipeline',
        primary: 'Pipeline beendet',
        variant: 'muted',
      };
    default:
      return null;
  }
}

function shortJson(v: unknown): string {
  if (v == null) return '';
  try {
    return JSON.stringify(v).slice(0, 60);
  } catch {
    return String(v).slice(0, 60);
  }
}

function Timeline({
  grouped,
  openId,
  setOpenId,
}: {
  grouped: GroupedEvent[];
  openId: string | null;
  setOpenId: (id: string | null) => void;
}) {
  const CARD_W = 200;
  const MIN_GAP = 220;
  const minTs = grouped[0]?.ts ?? 0;
  const maxTs = grouped[grouped.length - 1]?.ts ?? minTs + 1;
  const span = Math.max(1, maxTs - minTs);

  // Two-pass layout: time-proportional, then push-right to enforce
  // MIN_GAP per swim-lane so dense bursts don't overlap.
  const positions = React.useMemo(() => {
    const baseWidth = Math.max(grouped.length * MIN_GAP, 1200);
    const xs = grouped.map((ev, i) => {
      if (span < 500) return (i * (baseWidth - CARD_W)) / Math.max(1, grouped.length - 1);
      return ((ev.ts - minTs) / span) * (baseWidth - CARD_W);
    });
    const lastByLane: Record<string, number> = {};
    for (let i = 0; i < grouped.length; i++) {
      const ev = grouped[i];
      if (!ev) continue;
      const lane = ev.speaker;
      const prev = lastByLane[lane];
      const cur = xs[i] ?? 0;
      const adjusted = prev !== undefined && cur < prev + MIN_GAP ? prev + MIN_GAP : cur;
      xs[i] = adjusted;
      lastByLane[lane] = adjusted;
    }
    const totalWidth = Math.max(...xs, 0) + CARD_W + 40;
    return { xs, totalWidth: Math.max(totalWidth, 1200) };
  }, [grouped, minTs, span]);

  return (
    <div className="overflow-x-auto">
      <div className="relative" style={{ width: positions.totalWidth, minHeight: 380 }}>
        {/* Center axis */}
        <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-border" />

        {/* Events */}
        {grouped.map((ev, i) => {
          const left = positions.xs[i];
          const isUser = ev.speaker === 'user';
          const isAssist = ev.speaker === 'assistant';
          const isTool = ev.speaker === 'tool';
          const isSys = ev.speaker === 'system';
          const top = isUser ? 30 : isAssist ? 220 : isTool ? 130 : 130;
          return (
            <div
              key={ev.id}
              className="absolute"
              style={{ left, top, width: CARD_W }}
            >
              <button
                type="button"
                onClick={() => setOpenId(openId === ev.id ? null : ev.id)}
                className={cn(
                  'group w-full rounded-md border px-3 py-2 text-left transition-all',
                  isUser && 'border-sky-500/40 bg-sky-500/5 hover:bg-sky-500/10',
                  isAssist && 'border-primary/40 bg-primary/5 hover:bg-primary/10',
                  isTool && 'border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10',
                  isSys && 'border-border bg-muted/40 hover:bg-muted',
                  ev.variant === 'muted' && 'opacity-70',
                  openId === ev.id && 'ring-2 ring-ring',
                )}
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <ev.Icon className="h-3 w-3" />
                  <span>{ev.iconLabel}</span>
                  <span className="ml-auto font-mono">+{ev.ts}ms</span>
                </div>
                <div className="mt-1 line-clamp-2 text-sm font-medium">{ev.primary}</div>
                {ev.secondary && (
                  <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground font-mono">
                    {ev.secondary}
                  </div>
                )}
              </button>

              {/* Connector line to centerline */}
              <div
                className={cn(
                  'absolute left-1/2 h-3 w-px bg-border',
                  isUser ? 'top-full' : 'bottom-full',
                )}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KpiStrip({
  turns,
  tools,
  fillerInjections,
  latencyP50,
  duration,
}: {
  turns: number;
  tools: number;
  fillerInjections: number;
  latencyP50: number;
  duration: number;
}) {
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <Kpi label="Turns" value={turns.toString()} />
      <Kpi label="Tools" value={tools.toString()} />
      <Kpi label="Filler" value={fillerInjections.toString()} />
      <Kpi label="Latenz p50" value={latencyP50 ? `${latencyP50}ms` : '—'} />
      <Kpi label="Dauer" value={`${duration}s`} />
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}

function DetailPanel({
  event,
  onClose,
}: {
  event: CallEvent;
  onClose: () => void;
}) {
  const [showJson, setShowJson] = React.useState(true);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Event: {event.type}</CardTitle>
            <CardDescription>
              +{event.ts_ms ?? 0}ms ab Call-Start ·{' '}
              {event.iso_ts?.toDate?.()?.toLocaleString('de-DE') ?? ''}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <FriendlyEventBody event={event} />
        <Button size="sm" variant="ghost" onClick={() => setShowJson((s) => !s)}>
          {showJson ? <ChevronUp className="mr-1 h-3.5 w-3.5" /> : <ChevronDown className="mr-1 h-3.5 w-3.5" />}
          Roh-JSON {showJson ? 'ausblenden' : 'anzeigen'}
        </Button>
        {showJson && (
          <pre className="max-h-96 overflow-auto rounded-md border border-border bg-background p-3 font-mono text-xs">
            {JSON.stringify(event.data ?? {}, null, 2)}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

function FriendlyEventBody({ event }: { event: CallEvent }) {
  const data = (event.data ?? {}) as Record<string, unknown>;
  const blocks: Array<{ label: string; content: string }> = [];

  switch (event.type) {
    case 'call_started': {
      const sysPrompt = typeof data.systemPrompt === 'string' ? data.systemPrompt : '';
      if (sysPrompt) blocks.push({ label: 'System-Prompt', content: sysPrompt });
      const meta = [
        data.provider ? `Provider: ${data.provider}` : '',
        data.from ? `Anrufer: ${data.from}` : '',
        data.llmProvider && data.llmModel ? `LLM: ${data.llmProvider} / ${data.llmModel}` : '',
      ]
        .filter(Boolean)
        .join('\n');
      if (meta) blocks.push({ label: 'Metadaten', content: meta });
      break;
    }
    case 'user_transcript': {
      const text = typeof data.text === 'string' ? data.text : '';
      blocks.push({
        label: `Kunde sagte${data.language ? ` (${data.language})` : ''}`,
        content: text || '(leer)',
      });
      break;
    }
    case 'llm_response_complete': {
      const text = typeof data.text === 'string' ? data.text : '';
      blocks.push({
        label: 'LLM-Antwort (Volltext)',
        content: text || '(kein Text erfasst)',
      });
      const meta = [
        typeof data.charCount === 'number' ? `Zeichen: ${data.charCount}` : '',
        typeof data.durationMs === 'number' ? `Generierung: ${data.durationMs} ms` : '',
      ]
        .filter(Boolean)
        .join(' · ');
      if (meta) blocks.push({ label: 'Stats', content: meta });
      break;
    }
    case 'tts_stopped': {
      const text = typeof data.text === 'string' ? data.text : '';
      if (text) blocks.push({ label: 'Gesprochener Text', content: text });
      const meta = typeof data.durationMs === 'number' ? `${data.durationMs} ms TTS-Audio` : '';
      if (meta) blocks.push({ label: 'Stats', content: meta });
      break;
    }
    case 'tool_call': {
      blocks.push({
        label: `Tool-Aufruf: ${String(data.name ?? '?')}`,
        content: prettyJson(data.args),
      });
      break;
    }
    case 'tool_result': {
      blocks.push({
        label: `Tool-Ergebnis: ${String(data.name ?? '?')}`,
        content: prettyJson(data.result),
      });
      break;
    }
    case 'llm_first_token_latency': {
      blocks.push({
        label: 'LLM-Erstantwortzeit',
        content: `${data.ms ?? 0} ms vom Sprechende des Kunden bis zum ersten Token`,
      });
      break;
    }
    case 'filler_played': {
      blocks.push({
        label: 'Filler eingespielt',
        content: `${data.category ?? 'unbekannt'} (id: ${data.fillerId ?? '—'})`,
      });
      break;
    }
    default:
      break;
  }

  if (blocks.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Marker-Event ohne zusätzliche Nutzdaten — siehe Roh-JSON für Details.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {blocks.map((b, i) => (
        <div key={i} className="rounded-md border border-border bg-muted/30 p-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{b.label}</div>
          <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap font-mono text-xs">
            {b.content}
          </pre>
        </div>
      ))}
    </div>
  );
}

function prettyJson(v: unknown): string {
  if (v == null) return '(leer)';
  if (typeof v === 'string') {
    // tools.py often returns dicts that round-trip as JSON strings.
    try {
      return JSON.stringify(JSON.parse(v), null, 2);
    } catch {
      return v;
    }
  }
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
